"use client";

import * as React from 'react';
import { cn } from '../lib/utils';
import { FieldContext } from '../lib/field';
import { Label } from '../primitives/label';

export interface FormFieldProps extends React.ComponentPropsWithoutRef<'div'> {
  label?: React.ReactNode;
  description?: React.ReactNode;
  /** What is wrong and how to fix it. The control is marked invalid while set. */
  error?: React.ReactNode;
  /** The control's id. Generated when omitted; the control inside picks it up. */
  htmlFor?: string;
  required?: boolean;
  children?: React.ReactNode;
}

/**
 * A label, its control, help text and error. The shared controls inside
 * (Input, Textarea, Select, Checkbox, Switch) receive the id, the
 * description and error ids (aria-describedby) and aria-invalid from it.
 */
export function FormField({
  label,
  description,
  error,
  htmlFor,
  required = false,
  className,
  children,
  ...props
}: FormFieldProps) {
  const generated = React.useId();
  const id = htmlFor ?? `field-${generated}`;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const context = React.useMemo(
    () => ({
      id,
      describedBy: [descriptionId, errorId].filter(Boolean).join(' ') || undefined,
      invalid: Boolean(error),
      required,
    }),
    [id, descriptionId, errorId, error, required]
  );

  return (
    <div className={cn('space-y-1.5', className)} {...props}>
      {label ? (
        <Label htmlFor={id} className="block">
          {label}
          {required ? (
            <span className="ml-0.5 text-danger" aria-hidden="true">
              *
            </span>
          ) : null}
        </Label>
      ) : null}
      <FieldContext.Provider value={context}>{children}</FieldContext.Provider>
      {description ? (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
